import React, { Fragment, useState, useEffect, useCallback } from "react";
import { compose } from "recompose";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { InlineAlert } from "theme/components/molecules/Alert";
import Button from "theme/components/atoms/Button";
import withFormHandlers from "theme/components/atoms/Form/Input/withFormHandlers";
import { useShop } from "web/core/shop/ShopContext";
import { formatCurrency } from "theme/modules/User/CurrencySelector/formatCurrency";

const isNumber = (number) => !Number.isNaN(Number(number));

const parsePrice = (price) => {
  if (price === "") {
    return;
  }

  if (!isNumber(price)) {
    return;
  }

  return Number(price);
};

const isValid = (minPrice, maxPrice) => {
  return (
    (minPrice === "" || isNumber(minPrice)) &&
    (maxPrice === "" || isNumber(maxPrice))
  );
};

const usePriceRangeState = ({ value, onChange, applyOnChange }) => {
  const [{ minPrice, maxPrice }, setValue] = useState(value);

  useEffect(() => {
    setValue(value);
  }, [value]);

  const updateValue = (event) => {
    const newValue = {
      minPrice,
      maxPrice,
      [event.target.name]: event.target.value,
    };
    if (applyOnChange) {
      setValue(newValue);
    } else {
      onChange(newValue);
    }
  };

  const handleChange = useCallback(() => {
    let min = parsePrice(minPrice);
    let max = parsePrice(maxPrice);
    if (typeof min !== "undefined" && typeof max !== "undefined" && min > max) {
      [min, max] = [max, min];
    }

    onChange({ minPrice: min, maxPrice: max });
  }, [maxPrice, minPrice, onChange]);

  return {
    minPrice,
    maxPrice,
    updateValue,
    handleChange,
  };
};

const useErrorRangeState = ({ minPrice, maxPrice }) => {
  const [shouldShowInvalidPriceError, displayInvalidPriceError] =
    useState(false);

  useEffect(() => {
    if (shouldShowInvalidPriceError && isValid(minPrice, maxPrice)) {
      displayInvalidPriceError(false);
    }
  }, [shouldShowInvalidPriceError, minPrice, maxPrice]);

  return { shouldShowInvalidPriceError, displayInvalidPriceError };
};

const PriceFilter = (props) => {
  const shop = useShop();

  const { minPrice, maxPrice, updateValue, handleChange } = usePriceRangeState({
    value: props.value,
    onChange: props.onChange,
    applyOnChange: props.applyOnChange,
  });

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      handleChange();
    }
  };

  const { shouldShowInvalidPriceError, displayInvalidPriceError } =
    useErrorRangeState({ minPrice, maxPrice });

  return (
    <Fragment>
      <div className="price-filter">
        <FormattedMessage
          id="components.atoms.PriceFilter.from"
          defaultMessage="from"
        />
        <input
          name="minPrice"
          onChange={updateValue}
          onKeyDown={handleKeyDown}
          className="price-filter__input"
          value={minPrice || ""}
        />{" "}
        <FormattedMessage
          id="components.atoms.PriceFilter.to"
          defaultMessage="to"
        />
        <input
          name="maxPrice"
          onChange={updateValue}
          onKeyDown={handleKeyDown}
          className="price-filter__input"
          value={maxPrice || ""}
        />
        <span className="price-filter__currency">
          &nbsp;{formatCurrency(shop.currentCurrency)}
        </span>
        {props.applyOnChange ? (
          <Button
            size="small"
            onClick={handleChange}
            state={!isValid(minPrice, maxPrice) ? "disabled" : undefined}
            onDisableClick={() => {
              displayInvalidPriceError(true);
            }}
          >
            OK
          </Button>
        ) : null}
      </div>
      {shouldShowInvalidPriceError && (
        <InlineAlert appearance="error">
          <FormattedMessage
            id="components.atoms.PriceFilter.invalid"
            defaultMessage="An invalid number was given in your price range."
          />
        </InlineAlert>
      )}
    </Fragment>
  );
};

PriceFilter.propTypes = {
  min: PropTypes.string,
  max: PropTypes.string,
};

export default compose(
  withFormHandlers({
    dirtyOnChange: true,
    getValueFromEvent: (value) => value,
  })
)(PriceFilter);
